import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  Input,
  Modal,
  notification,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type {
  InputRef,
  TableColumnsType,
  TableColumnType,
  TableProps
} from 'antd'
import type { FilterDropdownProps, SortOrder } from 'antd/es/table/interface'
import _ from 'lodash'
import moment from 'moment'
import styled from 'styled-components'
import * as Icons from '@ant-design/icons'
import { colours } from '@qflow/theme'

import { Queries } from 'admin/lib/Api'
import { queryKeys } from 'admin/lib/Api/queries-common'
import { useManufacturerDelete, useSupplierDelete } from 'admin/lib/Api/queries'
import {
  hasCertExpired,
  isCertExpiringSoon
} from 'admin/lib/Certificates/CertificateExpiry'
import { CertificateTag } from 'admin/lib/Certificates/CertificateTag'

type OnChange = NonNullable<TableProps['onChange']>
type Filters = Parameters<OnChange>[1]
interface DataType {
  id: string
  isManufacturer: boolean
  number: string
  name: string
  address: string
  companyCertificates: any[]
  createdAt: string
}

type DataIndex = keyof DataType

type DeleteCompanyType = {
  id: string
  companyName: string
  isManufacturer: boolean
}

export function CompaniesList({
  onEdit
}: {
  onEdit: (
    companyId: string,
    isManufacturer: boolean,
    hasMatch: boolean
  ) => void
}) {
  const user = Queries.useUserSelf()
  const [stateFilters, setStateFilters] = useState<Filters>({})
  const [stateSorter, setStateSorter] = useState<{
    columnKey?: string
    order?: string
  }>({})
  const [deleteCompany, setDeleteCompany] = useState<DeleteCompanyType | null>(
    null
  )
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const deleteManufacturerApiCall = useManufacturerDelete()
  const deleteSupplierApiCall = useSupplierDelete()

  useEffect(() => {
    // Load saved filters and sorter from local storage
    const companiesFilters = localStorage.getItem('companiesFilters')
    const savedFilters = companiesFilters ? JSON.parse(companiesFilters) : {}
    const companiesSorter = localStorage.getItem('companiesSorter')
    const savedSorter = companiesSorter ? JSON.parse(companiesSorter) : {}
    setStateFilters(savedFilters)
    setStateSorter(savedSorter)

    if (savedFilters.name && savedFilters.name[0]) {
      setSearchText(savedFilters.name[0])
      setSearchedColumn('name')
    }
  }, [])

  useEffect(() => {
    // Save filters and sorter to local storage whenever they change
    localStorage.setItem('companiesFilters', JSON.stringify(stateFilters))
    localStorage.setItem('companiesSorter', JSON.stringify(stateSorter))
  }, [stateFilters, stateSorter])

  const smFilterOptions = useMemo(
    () => [
      { text: 'Supplier', value: 'supplier' },
      { text: 'Manufacturer', value: 'manufacturer' }
    ],
    []
  )

  const certificateFilterOptions = useMemo(
    () => [
      { text: 'No certificates', value: 'none' },
      { text: 'Has certificate(s)', value: 'some' },
      { text: 'Expired', value: 'expired' },
      { text: 'Expiring soon', value: 'expiring' }
    ],
    []
  )

  const companies = Queries.useSuppliersAndManufacturers(
    stateFilters,
    stateSorter
  )
  const allCompanies = useMemo(() => {
    if (!companies.data?.pages?.length) {
      return []
    }

    return _.flatMap(companies.data.pages, page => page)
  }, [companies.data])

  const queryClient = useQueryClient()
  const handleTableChange: TableProps<DataType>['onChange'] = (
    pagination,
    filters,
    sorter,
    extra
  ) => {
    // Retain stateFilters Name filter when updating
    if (filters.name && filters.name[0]) {
      setStateFilters(filters)
    } else {
      setStateFilters({ ...filters, name: stateFilters.name })
    }
    setStateSorter({
      columnKey: (Array.isArray(sorter)
        ? sorter[0]?.field
        : sorter.field) as string,
      order: (Array.isArray(sorter) ? sorter[0]?.order : sorter.order) as string
    })

    // Remove the getSuppliersAndManufacturers query so that it will be refetched with the new filters/sort
    queryClient.removeQueries({
      queryKey: [queryKeys.getSuppliersAndManufacturers]
    })
  }

  const handleEditButtonClick = useCallback(
    (companyId, isManufacturer, companyNumber, companyName) => {
      const hasMatch = allCompanies.some(
        company =>
          company.id !== companyId &&
          company.number === companyNumber &&
          company.name === companyName
      )
      onEdit(companyId, isManufacturer, hasMatch)
    },
    [allCompanies, onEdit]
  )

  const handleConfirmDelete = useCallback(async () => {
    const deleteCompanyId: string | undefined = deleteCompany?.id
    setDeleteCompany(null)

    if (!deleteCompanyId) {
      return
    }
    if (!deleteCompany?.isManufacturer) {
      try {
        await deleteSupplierApiCall.mutateAsync({ companyId: deleteCompanyId })

        notification.success({
          message: 'Supplier successfully removed! 🙌'
        })
      } catch (e: any) {
        notification.error({
          message:
            'Delete failed 😦. ' +
            e?.response.status +
            ': ' +
            String(e?.response.data.Message)
        })
      }
    } else {
      try {
        await deleteManufacturerApiCall.mutateAsync({
          companyId: deleteCompanyId
        })
        notification.success({
          message: 'Manufacturer successfully removed! 🙌'
        })
      } catch (e: any) {
        notification.error({
          message:
            'Delete failed 😦. ' +
            e?.response.status +
            ': ' +
            String(e?.response.data.Message)
        })
      }
    }
  }, [
    deleteCompany?.id,
    deleteCompany?.isManufacturer,
    deleteManufacturerApiCall,
    deleteSupplierApiCall
  ])

  const columns: TableColumnsType<DataType> = useMemo(() => {
    const handleSearch = (
      selectedKeys: string[],
      confirm: FilterDropdownProps['confirm'],
      dataIndex: DataIndex
    ) => {
      confirm()
      setSearchText(selectedKeys[0])
      setSearchedColumn(dataIndex)
    }

    const handleReset = (clearFilters: () => void) => {
      clearFilters()
      setSearchText('')

      const newFilters = { ...stateFilters }
      delete newFilters[searchedColumn]
      setStateFilters(newFilters)

      // Remove the getSuppliersAndManufacturers query so that it will be refetched with the new filters/sort
      queryClient.removeQueries({
        queryKey: [queryKeys.getSuppliersAndManufacturers]
      })
    }

    const getColumnSearchProps = (
      dataIndex: DataIndex
    ): TableColumnType<DataType> => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close
      }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0] || searchText}
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleSearch(selectedKeys as string[], confirm, dataIndex)
              }
              icon={<Icons.SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                if (clearFilters) {
                  handleReset(clearFilters)
                }
                close()
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                close()
              }}
            >
              close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (
        <Icons.SearchOutlined
          style={{
            color:
              searchText && searchedColumn === dataIndex
                ? colours.TEAL
                : undefined
          }}
        />
      ),
      onFilter: (value, record) =>
        record[dataIndex]
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase()),
      filterDropdownProps: {
        onOpenChange(open) {
          if (open) {
            setTimeout(() => searchInput.current?.select(), 100)
          }
        }
      }
    })

    const columns: TableColumnsType<DataType> = [
      {
        title: <Tooltip title="Supplier or Manufacturer">S/M</Tooltip>,
        dataIndex: 'isManufacturer',
        key: 'isManufacturer',
        width: '3rem',
        align: 'center',
        filters: smFilterOptions,
        filterMultiple: false,
        filteredValue: stateFilters.isManufacturer || null,
        onFilter: (value, record) => {
          switch (value) {
            case 'supplier':
              return !record.isManufacturer

            case 'manufacturer':
              return record.isManufacturer
          }

          return false
        },
        render: isManufacturer =>
          isManufacturer ? (
            <Tooltip title="Manufacturer">
              <StyledTag color="green">M</StyledTag>
            </Tooltip>
          ) : (
            <Tooltip title="Supplier">
              <StyledTag color="blue">S</StyledTag>
            </Tooltip>
          )
      },
      {
        title: 'Company Number',
        dataIndex: 'number',
        key: 'number',
        sorter: (a, b) => a.number.localeCompare(b.number),
        sortOrder:
          stateSorter.columnKey === 'number' && stateSorter.order
            ? (stateSorter.order as SortOrder)
            : undefined
      },
      {
        title: 'Company Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortOrder:
          stateSorter.columnKey === 'name' && stateSorter.order
            ? (stateSorter.order as SortOrder)
            : undefined,
        ...getColumnSearchProps('name')
      },
      {
        title: 'Address',
        dataIndex: 'address',
        key: 'address'
      },
      {
        title: 'Certificates',
        dataIndex: 'companyCertificates',
        key: 'companyCertificates',
        filters: certificateFilterOptions,
        filteredValue: stateFilters.companyCertificates || null,
        onFilter: (value, record) => {
          switch (value) {
            case 'none':
              return (
                !record.companyCertificates ||
                record.companyCertificates.length < 1
              )

            case 'some':
              return (
                record.companyCertificates &&
                record.companyCertificates.length > 0
              )

            case 'expired':
              return (
                record.companyCertificates &&
                record.companyCertificates.some(
                  cert => cert.expirationDate && hasCertExpired(cert)
                )
              )
            case 'expiring':
              return (
                record.companyCertificates &&
                record.companyCertificates.some(
                  cert =>
                    cert.expirationDate &&
                    !hasCertExpired(cert) &&
                    isCertExpiringSoon(cert)
                )
              )
          }

          return false
        },
        render: certificates =>
          certificates?.map(cert =>
            cert.certificationScheme ? (
              <CertificateTag
                key={cert.certificateId}
                id={cert.certificateId}
                cert={cert}
              />
            ) : (
              <Tag key={cert.certificateId} color="red">
                (Incomplete Certificate)
              </Tag>
            )
          )
      },
      {
        title: 'Created At',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '11rem',
        render: createdAt =>
          createdAt ? new Date(createdAt).toLocaleString() : '',
        sorter: (a, b) =>
          moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
        sortOrder:
          stateSorter.columnKey === 'createdAt' && stateSorter.order
            ? (stateSorter.order as SortOrder)
            : undefined
      },
      {
        title: <span>&nbsp;</span>,
        key: 'edit',
        width: '2rem',
        align: 'center',
        render: company => (
          <Tooltip title="Edit Company">
            <Button
              tabIndex={-1}
              icon={<Icons.EditFilled />}
              value={company.id}
              onClick={e =>
                handleEditButtonClick(
                  company.id,
                  company.isManufacturer,
                  company.number,
                  company.name
                )
              }
            />
          </Tooltip>
        )
      }
    ]
    // Show Delete button to Super Users
    if (user.data && user.data.isSuper) {
      const handleRemoveButtonClick = companyId => {
        const company = allCompanies.filter(company => {
          return company.id === companyId
        })[0]
        setDeleteCompany({
          id: companyId,
          companyName: company.name,
          isManufacturer: company.isManufacturer
        })
      }

      columns?.push({
        title: <span>&nbsp;</span>,
        dataIndex: 'id',
        key: 'companyId',
        width: '2rem',
        align: 'center',
        render: id => (
          <Tooltip title="Remove Company">
            <Button
              type="text"
              tabIndex={-1}
              danger
              icon={<Icons.DeleteOutlined />}
              value={id}
              onClick={e => handleRemoveButtonClick(id)}
            />
          </Tooltip>
        )
      })
    }

    return columns
  }, [
    allCompanies,
    certificateFilterOptions,
    handleEditButtonClick,
    queryClient,
    searchText,
    searchedColumn,
    smFilterOptions,
    stateFilters,
    stateSorter,
    user.data
  ])

  return (
    <>
      {companies.isError && <div>{String(companies.error)}</div>}

      <Table<DataType>
        columns={columns}
        loading={companies.isLoading}
        dataSource={allCompanies}
        onChange={handleTableChange}
        rowKey={record => record.id}
        pagination={false}
        style={{ width: '100%', height: '100%' }}
      />
      <Row justify="center">
        <Button
          type="primary"
          onClick={() => companies.fetchNextPage()}
          disabled={companies.isFetchingNextPage || !companies.hasNextPage}
          size="large"
          style={{
            width: '15rem',
            margin: '1rem 0'
          }}
          icon={
            companies.isFetchingNextPage ? (
              <Icons.LoadingOutlined />
            ) : (
              <Icons.ArrowDownOutlined />
            )
          }
        >
          {companies.isFetchingNextPage ? 'Loading...' : 'Load More'}
        </Button>
      </Row>

      <Modal
        title={`Are you sure you want to remove this ${deleteCompany?.isManufacturer ? 'manufacturer' : 'supplier'}?`}
        open={deleteCompany ? true : false}
        onCancel={e => setDeleteCompany(null)}
        onOk={handleConfirmDelete}
        destroyOnHidden
      >
        <p>
          <Typography.Text>
            {deleteCompany ? deleteCompany.companyName : ''}
          </Typography.Text>
        </p>
        <p>
          <Typography.Text>
            This operation cannot be undone. Click 'OK' to delete this{' '}
            {deleteCompany?.isManufacturer ? 'manufacturer' : 'supplier'}.
          </Typography.Text>
        </p>{' '}
      </Modal>
    </>
  )
}

const StyledTag = styled(Tag)`
  border-radius: 1rem;
  min-width: 1.75rem;
  text-align: center;
  min-height: 1.75rem;
  align-content: center;
`
